/**
 * Hook: load a guide article module by (section, slug, lang).
 *
 * Returns `{ article, loading, error }`.  Modules are split into per-article
 * chunks via Vite glob+dynamic-import — first visit triggers a network fetch,
 * subsequent visits hit the in-memory cache.
 *
 * Synchronous state (no-input, cached hit, missing module) is derived via
 * useMemo so we never call setState synchronously inside an effect.  Only
 * the async resolution path updates state — and only after the loader's
 * promise settles.
 */

import { useEffect, useMemo, useState } from 'react';
import { findArticleLoader } from './content-registry';
import type { ArticleSlug, LangCode, SectionSlug } from './architecture';
import type { GuideArticle } from './content-types';

interface UseArticleResult {
  article: GuideArticle | null;
  loading: boolean;
  error: Error | null;
}

const memo = new Map<string, GuideArticle>();

function keyOf(lang: LangCode, section: SectionSlug, slug: ArticleSlug): string {
  return `${lang}/${section}/${slug}`;
}

interface SyncOutcome {
  /** Cached article, if any. */
  cached: GuideArticle | null;
  /** True when a loader exists and we need to fetch. */
  willFetch: boolean;
  /** Cache key for this combination. */
  key: string | null;
}

function deriveSync(
  lang: LangCode,
  section: SectionSlug | null,
  slug: ArticleSlug | null,
): SyncOutcome {
  if (!section || !slug) return { cached: null, willFetch: false, key: null };
  const k = keyOf(lang, section, slug);
  const cached = memo.get(k);
  if (cached) return { cached, willFetch: false, key: k };
  const loader = findArticleLoader(lang, section, slug);
  return { cached: null, willFetch: !!loader, key: k };
}

export function useGuideArticle(
  lang: LangCode,
  section: SectionSlug | null,
  slug: ArticleSlug | null,
): UseArticleResult {
  const sync = useMemo(
    () => deriveSync(lang, section, slug),
    [lang, section, slug],
  );

  // Async state — only updated after a fetch settles.  Keyed by the cache key
  // so a stale resolution from a previous (lang, section, slug) is discarded.
  const [resolved, setResolved] = useState<{ key: string; article: GuideArticle | null; error: Error | null } | null>(null);

  useEffect(() => {
    if (!sync.willFetch || !sync.key || !section || !slug) return;
    const loader = findArticleLoader(lang, section, slug);
    if (!loader) return;
    let cancelled = false;
    loader()
      .then((mod) => {
        if (cancelled) return;
        memo.set(sync.key!, mod.default);
        setResolved({ key: sync.key!, article: mod.default, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setResolved({
          key: sync.key!,
          article: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [sync.willFetch, sync.key, lang, section, slug]);

  // Cached hit — return immediately.
  if (sync.cached) {
    return { article: sync.cached, loading: false, error: null };
  }
  // No loader for this combo — settled empty.
  if (!sync.willFetch) {
    return { article: null, loading: false, error: null };
  }
  // Loader exists — either resolved (for this key) or still loading.
  if (resolved && resolved.key === sync.key) {
    return { article: resolved.article, loading: false, error: resolved.error };
  }
  return { article: null, loading: true, error: null };
}
